<template>
	<div class="balance_level_detail_tab report_theme_container" data-theme="resultados">
		<TabHeader
			:title="title"
			:description="description"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			:compact="true"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			:table-title="chartsSectionTitle"
			table-title-collapsible
			:table-expanded="chartsExpanded"
			@toggle-table="toggleCharts"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange" />

		<!-- Sección de gráficos plegable, mismo patrón que Ventas (el toggle
			vive en el título de la barra del header; el cuerpo headless abajo).
			Arranca PLEGADA. Por ahora dos placeholders que reservan el espacio
			de los gráficos, como los cards "Próximamente" de KPIs. -->
		<CollapsibleSection class="mt-1"
			headless
			:controlled-expanded="chartsExpanded">
			<div class="charts_grid">
				<article class="chart_card chart_card--placeholder">
					<header class="chart_card_header">
						<span class="chart_card_title">Composición por cuenta</span>
					</header>
					<div class="chart_state">Próximamente</div>
				</article>
				<article class="chart_card chart_card--placeholder">
					<header class="chart_card_header">
						<span class="chart_card_title">Evolución del saldo</span>
					</header>
					<div class="chart_state">Próximamente</div>
				</article>
			</div>
		</CollapsibleSection>

		<!-- Misma tabla del Resumen de Balance, acotada a UN nivel 1 -->
		<div class="underHeader">
			<FinancialResultsTable
				class="mt-4"
				:rows="tableRows"
				:headers="monthHeaders"
				:isLoading="isLoading"
				:filterPeriod="filterPeriod"
				:expandedRows="expandedRowIds"
				:show-period-select="false"
				@toggle-childs="toggleL2" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapActions, mapMutations } from "vuex";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";

import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";

// Mismo lenguaje de color que el Resumen de Balance para la banda de nivel 1.
const L1_THEME = {
	Activos: "theme_row_azure",
	Pasivos: "theme_row_blue",
	Patrimonio: "theme_row_blue",
};

// Tab de balance por nivel para empresas de MAYOR (ERP): la misma tabla del
// Resumen de Balance (FinancialResultsTable + módulo balanceReport) pero
// acotada a UN grupo de nivel 1 (Activos/Pasivos/Patrimonio) y con todo el
// nivel 3 DESPLEGADO por defecto (el usuario puede plegar por L2). Las
// empresas de clasificador/SII siguen usando BalanceLevelTab (saldos con
// filtro de contraparte) — el switch por has_ledger vive en DashboardReports.
export default {
	name: "BalanceLevelDetailTab",

	components: {
		TabHeader,
		CollapsibleSection,
		FinancialResultsTable,
	},

	props: {
		// Nombre del grupo de nivel 1 a mostrar (Activos/Pasivos/Patrimonio),
		// tal como lo devuelve el backend en balanceReport.groups.
		level1: { type: String, required: true },
		title: { type: String, required: true },
		description: { type: String, default: "" },
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			// Inverso al Resumen: acá el L3 arranca DESPLEGADO, así que
			// trackeamos las L2 que el usuario plegó (vacío → todo abierto).
			// Sobrevive a refetches: una L2 nueva del backend nace desplegada.
			collapsedRowIds: [],
			// Sección de gráficos: PLEGADA por default (los gráficos reales
			// todavía no existen — placeholders "Próximamente").
			chartsExpanded: false,
		};
	},

	computed: {
		...mapState("reportsFilters", ["dataFirstMonth", "endMonth", "selectedPeriodRange", "customRange", "customYearRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("balanceReport", ["groups", "accountsData", "monthHeaders", "isLoading"]),

		// Grupo de nivel 1 de este tab. Puede no existir aún (primer fetch en
		// vuelo) → la tabla queda vacía con su estado de carga.
		group() {
			return this.groups.find((g) => g.level1 === this.level1) || null;
		},

		// Todas las L2 del grupo menos las plegadas por el usuario — el default
		// "todo el nivel 3 a la vista" que pide el flujo de mayor.
		expandedRowIds() {
			if (!this.group) return [];
			return this.group.level2Accounts
				.map((l2) => `l2:${l2}`)
				.filter((id) => !this.collapsedRowIds.includes(id));
		},

		// Mismas filas que arma el Resumen de Balance para este grupo: banda de
		// nivel 1 (total calculado = suma de sus L2), luego cada L2 y, si está
		// expandida, sus cuentas L3.
		tableRows() {
			if (!this.group) return [];
			const rows = [];
			const l2List = this.group.level2Accounts;
			rows.push({
				id: `l1:${this.group.level1}`,
				label: this.group.level1,
				theme: L1_THEME[this.group.level1] || "theme_row_blue",
				isMoney: true,
				itemsDict: this.sumItems(l2List),
			});

			l2List.forEach((l2) => {
				const childrenL3 = (this.group.hierarchy[l2] || []).filter((l3) => l3 !== l2);
				const l2RowId = `l2:${l2}`;
				rows.push({
					id: l2RowId,
					label: l2,
					theme: "theme_row_white",
					isMoney: true,
					itemsDict: this.accountsData[l2] || {},
					hasChilds: childrenL3.length > 0,
				});

				if (this.expandedRowIds.includes(l2RowId)) {
					childrenL3.forEach((l3) => {
						rows.push({
							id: `l3:${l2}:${l3}`,
							label: l3,
							theme: "theme_row_detail_bills",
							isMoney: true,
							itemsDict: this.accountsData[l3] || {},
						});
					});
				}
			});
			return rows;
		},

		getNow() {
			return moment().toDate();
		},

		// Título de la barra plegable de gráficos, al modo de Ventas
		// ("Ventas por cuenta"): "Activos por cuenta" / "Pasivos por cuenta" /
		// "Patrimonio por cuenta".
		chartsSectionTitle() {
			return `${this.title} por cuenta`;
		},

		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},

		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},

		// Firma reactiva consolidada — mismo criterio que el Resumen de Balance.
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},

	watch: {
		fetchSignature() {
			this.fetchBalanceData();
		},
	},

	mounted() {
		this.fetchBalanceData();
	},

	methods: {
		...mapActions("balanceReport", ["fetchBalanceData"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		// Total del nivel 1 = suma por cabecera de sus L2 (calculado, no leído
		// del backend, para quedar consistente con las filas visibles).
		sumItems(labels) {
			const itemsDict = {};
			this.monthHeaders.forEach((header) => {
				itemsDict[header] = labels.reduce((sum, label) => {
					const val = Number(this.accountsData[label]?.[header] || 0);
					return sum + (isNaN(val) ? 0 : val);
				}, 0);
			});
			return itemsDict;
		},

		toggleCharts() {
			this.chartsExpanded = !this.chartsExpanded;
		},

		toggleL2(rowId) {
			const index = this.collapsedRowIds.indexOf(rowId);
			if (index > -1) {
				this.collapsedRowIds.splice(index, 1);
			} else {
				this.collapsedRowIds.push(rowId);
			}
		},

		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
	},
};
</script>

<style scoped lang="scss">
.balance_level_detail_tab {
	position: relative;
	// Mismo respiro inferior que el Resumen de Balance.
	padding-bottom: 24px;
	height: 100%;
}

// Placeholders de la sección de gráficos: mismo lenguaje que los cards
// "Próximamente" de KPIs (borde dashed, texto muted), en fila de a dos como
// los gráficos del header de Ventas.
.charts_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: 340px;
	gap: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
}

.chart_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.chart_card--placeholder {
	border-style: dashed;
}

.chart_card_header {
	flex-shrink: 0;
	margin-bottom: 8px;
}

.chart_card_title {
	color: var(--report-text);
	font-size: 14px;
	font-weight: 600;
}

.chart_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;
}

// Header común vive en TabHeader.vue.
</style>
